import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaCalendarAlt, FaClock, FaVideo, FaPhone, FaMapMarkerAlt, FaUser, FaClipboard, FaMoneyBillWave, FaChevronLeft } from 'react-icons/fa';
import { authAPI } from '../../services/api';
import expert1 from '../../assets/team-4.jpg';
import './expert.css';

export default function AppointmentDetails() {
  const { id } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch appointment details from API
  useEffect(() => {
    if (id) {
      fetchAppointmentDetails();
    }
  }, [id]);

  const fetchAppointmentDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await authAPI.getExpertAppointmentDetails(id);
      
      if (result.success) {
        setAppointment(result.data);
      } else {
        setError(result.message || 'Failed to fetch appointment details');
      }
    } catch (err) {
      console.error('Error fetching appointment details:', err);
      setError('Failed to load appointment details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format currency with Nigerian naira symbol
  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount).toLocaleString()}`;
  };

  // Format date and time
  const formatDateTime = (dateString, timeString) => {
    const date = new Date(dateString);
    const time = new Date(timeString);
    
    const dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    
    return {
      date: date.toLocaleDateString('en-US', dateOptions),
      time: time.toLocaleTimeString('en-US', timeOptions)
    };
  };

  // Loading component
  if (loading) {
    return (
      <div className="expert-dashboard-container-expt">
        <div className="loading-container-expt">
          <div className="loading-spinner-expt"></div>
          <p>Loading appointment details...</p>
        </div>
      </div>
    );
  }

  // Error component
  if (error) {
    return (
      <div className="expert-dashboard-container-expt">
        <div className="error-container-expt">
          <p className="error-message-expt">{error}</p>
          <button 
            className="retry-btn-expt" 
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No appointment found
  if (!appointment) {
    return (
      <div className="expert-dashboard-container-expt">
        <div className="error-container-expt">
          <p className="error-message-expt">Appointment not found</p>
          <Link to="/expert-dashboard/appointments" className="retry-btn-expt">
            Back to Appointments
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="expert-dashboard-container-expt">
      <div className="sell-header_ffx">
        <Link to="/expert-dashboard/appointments" className="back-link-expt">
          <FaChevronLeft /> Back to Appointments
        </Link>
        <h1 className='sell-title_ffx'>Appointment Details</h1>
      </div>

      <div className="appointment-detail-card-expt">
        <div className="appointment-client-info-expt">
          <img 
            src={appointment.client?.image || expert1} 
            alt={appointment.client?.name} 
            className="client-image-large-expt" 
          />
          <div className="client-details-expt">
            <h2>{appointment.client?.name}</h2>
            <p className="appointment-topic-expt">{appointment.description}</p>
            <div className="appointment-status-badge-expt">
              {appointment.status === 'upcoming' && (
                <span className="upcoming-badge-expt">Upcoming</span>
              )}
              {appointment.status === 'completed' && (
                <span className="completed-badge-expt">Completed</span>
              )}
              {appointment.status === 'cancelled' && (
                <span className="cancelled-badge-expt">Cancelled</span>
              )}
            </div>
          </div>
        </div>

        <div className="appointment-detail-grid-expt">
          <div className="appointment-time-section-expt">
            <h3>Appointment Time</h3>
            <div className="detail-item-expt">
              <FaCalendarAlt className="detail-icon-expt" />
              <span>{formatDateTime(appointment.date, appointment.time).date}</span>
            </div>
            <div className="detail-item-expt">
              <FaClock className="detail-icon-expt" />
              <span>{formatDateTime(appointment.date, appointment.time).time}</span>
            </div>
            <div className="detail-item-expt">
              {appointment.consultation_type === 'video' ? (
                <FaVideo className="detail-icon-expt" />
              ) : (
                <FaPhone className="detail-icon-expt" />
              )}
              <span>
                {appointment.consultation_type === 'video' ? 'Video Call' : 
                 appointment.consultation_type === 'voice' ? 'Voice Call' : 'Chat'}
              </span>
            </div>
          </div>

          <div className="client-contact-section-expt">
            <h3>Client Contact</h3>
            <div className="detail-item-expt">
              <FaUser className="detail-icon-expt" />
              <span>{appointment.client?.name}</span>
            </div>
            {appointment.client?.phone && (
              <div className="detail-item-expt">
                <FaPhone className="detail-icon-expt" />
                <span>{appointment.client.phone}</span>
              </div>
            )}
            <div className="detail-item-expt">
              <FaMapMarkerAlt className="detail-icon-expt" />
              <span>{appointment.client?.email}</span>
            </div>
          </div>

          <div className="farm-details-section-expt">
            <h3>Appointment Details</h3>
            <div className="detail-item-expt">
              <FaClipboard className="detail-icon-expt" />
              <span>ID: {appointment.payment_reference}</span>
            </div>
            <div className="detail-item-expt">
              <FaClipboard className="detail-icon-expt" />
              <span>Created: {new Date(appointment.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="payment-section-expt">
            <h3>Payment</h3>
            <div className="detail-item-expt">
              <FaMoneyBillWave className="detail-icon-expt" />
              <span>Amount: {formatCurrency(appointment.amount_paid)}</span>
            </div>
            <div className="detail-item-expt">
              <span>Status: </span>
              <span className="paid-badge-expt">Paid</span>
            </div>
          </div>
        </div>

        <div className="appointment-notes-section-expt">
          <h3>Consultation Details</h3>
          <p>{appointment.description}</p>
        </div>

        <div className="appointment-actions-expt">
          {appointment.status === 'upcoming' && (
            <>
              <button className="primary-btn-expt">
                Start Appointment
              </button>
              <button className="secondary-btn-expt">
                Request Reschedule
              </button>
              <button className="danger-btn-expt">
                Cancel Appointment
              </button>
            </>
          )}
          {appointment.status === 'completed' && (
            <>
              <button className="primary-btn-expt">
                View Consultation Report
              </button>
              <button className="secondary-btn-expt">
                Follow Up Appointment
              </button>
            </>
          )}
          {appointment.status === 'cancelled' && (
            <>
              <button className="secondary-btn-expt">
                Reschedule Appointment
              </button>
            </>
          )}
          <Link 
            to={`/expert-dashboard/messages?client_name=${encodeURIComponent(appointment.client?.name || '')}&appointment_id=${appointment.appointment_id}`} 
            className="message-client-btn-expt"
          >
            Message Client
          </Link>
        </div>
      </div>
    </div>
  );
}